import React, { useEffect } from "react";
import {
  Container,
  Typography,
  Grid,
  Box,
  Button,
  Card,
  CardContent,
  CardActions,
  Chip,
  Breadcrumbs,
  Link,
  Tabs,
  Tab,
  Paper,
  Pagination,
  TextField,
  InputAdornment,
  IconButton,
} from "@mui/material";
import {
  DirectionsBus,
  Search as SearchIcon,
  Clear as ClearIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useFirebaseAuth } from "../../hooks/useFirebaseAuth";
import { useState } from "react";
import { useToast } from "../../components/Toast";
import { Loading } from "../../components/Loading";
import { getValidToken } from "../../utils/authUtils";

interface Vehicle {
  id: string;
  vehicleNumber: string;
  vehicleType: string;
  bodyType: string;
  status: "Available" | "On Route" | "Under Maintenance";
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`vehicle-tabpanel-${index}`}
      aria-labelledby={`vehicle-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const fetchVehicles = async ({
  showToast,
  navigate,
  setIsLoading,
  setVehicles,
  setTotalPages,
  page,
  status,
  search,
}: {
  showToast: any;
  navigate: any;
  setIsLoading: any;
  setVehicles: any;
  setTotalPages: any;
  page: any;
  status: string;
  search: string;
}) => {
  setIsLoading(true);
  try {
    const token = await getValidToken();
    const queryParams = new URLSearchParams({
      page: page.toString(),
      limit: "10",
      status: status,
      ...(search && { search }),
    });

    const url = `${process.env.REACT_APP_API_HOST}vehicles/v2/get?${queryParams}`;
    const response = await axios.get(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (response.data) {
      if (response.data.pagination?.total > 0) {
        setVehicles(response.data.data || []);
        setTotalPages(response.data.pagination?.pages || 1);
      } else {
        setVehicles([]);
        setTotalPages(1);
      }
    }
  } catch (error) {
    return [];
  } finally {
    setIsLoading(false);
  }
};

const SelectVehicle = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useFirebaseAuth();
  const { showToast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [tabValue, setTabValue] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeSearch, setActiveSearch] = useState("");

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setPage(1); // Reset to first page when changing tabs
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleSearch = () => {
    setActiveSearch(searchQuery);
    setPage(1); // Reset to first page when search changes
  };

  const handleClearSearch = () => {
    setSearchQuery("");
    setActiveSearch("");
    setPage(1);
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      handleSearch();
    }
  };

  useEffect(() => {
    if (!authLoading) {
      fetchVehicles({
        showToast,
        navigate,
        setIsLoading,
        setVehicles,
        setTotalPages,
        page,
        status: tabValue === 0 ? "Available" : "Unavailable",
        search: activeSearch,
      });
    }
  }, [user, authLoading, page, tabValue, activeSearch]);

  if (authLoading || isLoading) {
    return <Loading message="Loading vehicles..." fullScreen={true} />;
  }

  const handleVehicleSelect = () => {
    navigate(`/trip/daily-bus/basic-details`);
  };

  const VehicleGrid = ({ vehicles }: { vehicles: Vehicle[] }) => (
    <Grid container spacing={3}>
      {vehicles.map((vehicle) => (
        <Grid item xs={12} sm={6} md={4} key={vehicle.id}>
          <Card
            sx={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
              position: "relative",
              opacity: vehicle.status !== "Available" ? 0.7 : 1,
            }}
          >
            <Box
              sx={{
                position: "absolute",
                top: 16,
                right: 16,
              }}
            >
              {vehicle.status}
            </Box>
            <CardContent sx={{ flexGrow: 1 }}>
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: "50%",
                  backgroundColor: "#ffd700",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mb: 2,
                }}
              >
                <DirectionsBus sx={{ fontSize: 40, color: "#000" }} />
              </Box>
              <Typography variant="h6" gutterBottom>
                {vehicle.vehicleNumber}
              </Typography>
              <Typography color="text.secondary" gutterBottom>
                {vehicle.vehicleType}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {vehicle.bodyType}
              </Typography>
            </CardContent>
            <CardActions>
              <Button
                fullWidth
                variant="contained"
                onClick={handleVehicleSelect}
                disabled={vehicle.status !== "Available"}
              >
                {vehicle.status === "Available" ? "Book Now" : vehicle.status}
              </Button>
            </CardActions>
          </Card>
        </Grid>
      ))}
    </Grid>
  );

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box
        mb={4}
        display="flex"
        alignItems="center"
        gap={2}
        justifyContent="flex-start"
      >
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link color="inherit" onClick={handleBack} sx={{ cursor: "pointer" }}>
            Trips
          </Link>
          <Typography color="text.primary">Select Vehicle</Typography>
        </Breadcrumbs>
      </Box>

      {/* Search and Tabs Container */}
      <Box>
        {/* Search Field */}
        <Paper sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search by vehicle number..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={handleKeyPress}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: searchQuery && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={handleClearSearch}
                      edge="end"
                      aria-label="clear search"
                    >
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              size="small"
            />
            <Button
              variant="contained"
              onClick={handleSearch}
              sx={{ minWidth: "100px" }}
            >
              Search
            </Button>
          </Box>
          {activeSearch && (
            <Box sx={{ mt: 1, display: "flex", alignItems: "center", gap: 1 }}>
              <Chip
                label={`Search: ${activeSearch}`}
                onDelete={handleClearSearch}
                size="small"
              />
            </Box>
          )}
        </Paper>

        {/* Tabs */}
        <Paper>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            indicatorColor="primary"
            textColor="primary"
            variant="fullWidth"
          >
            <Tab
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <span>Available</span>
                  <Chip
                    label={
                      vehicles.filter((v) => v.status === "Available").length
                    }
                    size="small"
                    color="success"
                  />
                </Box>
              }
            />
            <Tab
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <span>Unavailable</span>
                  <Chip
                    label={
                      vehicles.filter((v) => v.status !== "Available").length
                    }
                    size="small"
                    color="warning"
                  />
                </Box>
              }
            />
          </Tabs>
        </Paper>
      </Box>

      {vehicles.length > 0 ? (
        <>
          {/* Tab Panels */}
          <TabPanel value={tabValue} index={0}>
            <VehicleGrid
              vehicles={vehicles.filter((v) => v.status === "Available")}
            />
          </TabPanel>
          <TabPanel value={tabValue} index={1}>
            <VehicleGrid
              vehicles={vehicles.filter((v) => v.status !== "Available")}
            />
          </TabPanel>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box display="flex" justifyContent="center" mt={4}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, value) => setPage(value)}
                color="primary"
              />
            </Box>
          )}
        </>
      ) : (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
          <Box display="flex" justifyContent="center" mt={4}>
            <Typography variant="h6">No vehicles found</Typography>
          </Box>
        </Container>
      )}
    </Container>
  );
};

export default SelectVehicle;
